/**
 * OvertimeBanner — единая info-плашка «факт vs норма» для табеля (task 17 §5.5).
 *
 * Используется в трёх местах:
 *  - `AdminTimesheetBackfillPage` SignBar (desktop backfill);
 *  - `FieldSignPage` DeclaredSection (mobile sign);
 *  - `TabelEditorSheet` (mobile, редактор табеля).
 *
 * Контракт:
 *  - Норма (`normHoursHalf`) и факт (`actualHoursHalf`) — в полу-часах,
 *    приходят пропсами из уже-загруженного snapshot-payload'а (`default_declared_hours_half`
 *    у admin/field timesheet). Никаких fetch'ей внутри.
 *  - Два состояния: `over` (actual > norm) и `under` (actual < norm) — янтарный
 *    dashed-warning с текстом и нормой. На `match` (равно) компонент возвращает
 *    `null` (§20/13.2): дубль значения «X ч — ровно по норме дня» был визуальным
 *    шумом под крупной цифрой declared.
 */
import * as React from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle } from "lucide-react";

import { formatHoursHalf } from "@/lib/timesheet";

export type OvertimeBannerState = "over" | "under" | "match";

type RenderedState = Exclude<OvertimeBannerState, "match">;

export interface OvertimeBannerProps {
  /** Норма дня по производственному календарю, в полу-часах. */
  normHoursHalf: number;
  /** Фактическое значение табеля, в полу-часах. */
  actualHoursHalf: number;
  className?: string;
  "data-testid"?: string;
  /**
   * Показывать ли строку «Потребуется согласование с руководителем» в `over`.
   * Для surface мастера (`TimesheetDayEditor`) она бессмысленна — он сам
   * согласующий, поэтому передаётся `false`. На surface рабочего — `true`.
   */
  showApprovalNote?: boolean;
}

export function OvertimeBanner({
  normHoursHalf,
  actualHoursHalf,
  className,
  "data-testid": testId = "overtime-banner",
  showApprovalNote = true,
}: OvertimeBannerProps): React.ReactElement | null {
  const { t } = useTranslation();
  const state: OvertimeBannerState =
    actualHoursHalf > normHoursHalf ? "over" : actualHoursHalf < normHoursHalf ? "under" : "match";

  // §20/13.2: на match дубль значения «X ч — ровно по норме дня» под крупной
  // цифрой declared не несёт информации — компонент молчит.
  if (state === "match") {
    return null;
  }

  const renderedState: RenderedState = state;
  const params = {
    hours: formatHoursHalf(actualHoursHalf, "ч"),
    norm: formatHoursHalf(normHoursHalf, "ч"),
  };
  const text = t(`field.tabel.warning.${renderedState}`, params);

  const styleByState: Record<RenderedState, React.CSSProperties> = {
    over: {
      background: "var(--bg-panel-2)",
      color: "#7a4a05",
      border: "1px dashed #d4a85d",
      borderRadius: "var(--radius-card)",
    },
    under: {
      background: "var(--bg-panel-2)",
      color: "#7a4a05",
      border: "1px dashed #d4a85d",
      borderRadius: "var(--radius-card)",
    },
  };

  return (
    <div
      data-testid={testId}
      data-state={renderedState}
      role="status"
      className={`flex items-start gap-2 px-3 py-2 text-[12px] leading-snug ${className ?? ""}`}
      style={styleByState[renderedState]}
    >
      {renderedState === "over" && (
        <AlertTriangle
          className="mt-0.5 h-4 w-4 shrink-0"
          style={{ color: "#b45309" }}
          aria-hidden
        />
      )}
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <span>{text}</span>
        {renderedState === "over" && showApprovalNote && (
          <span style={{ opacity: 0.78 }}>{t("field.tabel.warning.overApprovalNote")}</span>
        )}
      </div>
    </div>
  );
}

/** Тонкий селектор нормы дня из snapshot-payload'а (admin + field). */
export function selectDayNormHoursHalf(
  payload: { default_declared_hours_half: number } | null | undefined,
): number {
  return payload?.default_declared_hours_half ?? 0;
}
